/**
 * useUsps — shared USPS Addresses v3.2 validation hook.
 *
 * Port of legacy `services/UspsAddressService.ts` (214L) adapted to v3 api-client.
 * Reused by RAO PersonModal (primary + mailing address capture), and designed
 * so RCP Apply Now, RCM, RIM can adopt the exact same hook.
 *
 * Behaviors preserved from legacy:
 *   - `autoFillFromZip`: ZIP -> {city, state} lookup on ZIP blur (5 digits).
 *   - `validateAddress`: full DPV confirmation with corrections + ZIPPlus4.
 *   - DPV label map ported verbatim (`Y/D/S/N` -> badge color + text).
 *
 * Behaviors adapted for v3:
 *   - Calls `client.usps.address` / `client.usps.cityState` (Function MI-backed).
 *   - All results are surfaced via React state so callers can drive UI inline.
 *   - AbortController lifetime tied to the consumer component via `useApi`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UspsAddressResponse } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';

export type DpvConfirmation = 'Y' | 'D' | 'S' | 'N' | '';

export interface DpvInfo {
  label: string;
  color: string;
  bg: string;
}

/** DPV badge text + brand-token colors (ported from legacy DPV_LABELS). */
export const DPV_LABELS: Record<string, DpvInfo> = {
  Y: { label: 'Confirmed', color: 'var(--on-brand)', bg: 'var(--ok-fill)' },
  D: { label: 'Confirmed - Missing Secondary', color: 'var(--on-brand)', bg: 'var(--brand-fill)' },
  S: { label: 'Confirmed - Invalid Secondary', color: 'var(--ink-600)', bg: 'var(--warn-bg)' },
  N: { label: 'Not Confirmed', color: 'var(--on-brand)', bg: 'var(--err-fill)' },
};

export interface UspsAddressInput {
  streetAddress: string;
  secondaryAddress?: string;
  city?: string;
  state: string;
  ZIPCode?: string;
}

export interface UseUspsState {
  /** DPV-confirmed result (or undefined before any validate call). */
  result: UspsAddressResponse | undefined;
  /** In-flight flag for full-address validation. */
  verifying: boolean;
  /** In-flight flag for ZIP -> city/state autofill. */
  autoFilling: boolean;
  /** Last error from USPS (mapped from Function envelope). */
  error: string | null;
  /** Full address validation. */
  validateAddress: (q: UspsAddressInput) => Promise<UspsAddressResponse | undefined>;
  /** ZIP -> {city,state,ZIPCode}. Returns undefined on not-found. */
  autoFillFromZip: (zip: string) => Promise<{ city: string; state: string; ZIPCode: string } | undefined>;
  /** Clear any stored result + error. */
  reset: () => void;
}

/**
 * Flatten whatever USPS (or our Function proxy) hands back into a human string.
 *
 * USPS v3 API returns error envelopes of the form `{code, message, errors[]}`
 * either at top level or nested under `.error`. Historically useUsps stored
 * `resp.error` directly in state, which worked when it was a string — but when
 * it's the object form, rendering `{usps.error}` in JSX throws React #31
 * ("object with keys {code, message, errors}") and the ErrorBoundary catches it
 * as a generic "Something went wrong." This normalizer guarantees we only ever
 * put a string (or null) into `error` state.
 */
function coerceUspsError(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw || null;
  if (typeof raw === 'object') {
    const obj = raw as { message?: unknown; code?: unknown; errors?: unknown };
    const parts: string[] = [];
    if (typeof obj.message === 'string' && obj.message) parts.push(obj.message);
    if (Array.isArray(obj.errors)) {
      for (const e of obj.errors) {
        if (typeof e === 'string') parts.push(e);
        else if (e && typeof e === 'object') {
          const em = (e as { message?: unknown }).message;
          if (typeof em === 'string' && em) parts.push(em);
        }
      }
    }
    if (parts.length > 0) return parts.join(' — ');
    if (typeof obj.code === 'string' && obj.code) return `USPS ${obj.code}`;
    try { return JSON.stringify(raw); } catch { return 'USPS validation failed'; }
  }
  return String(raw);
}

export function useUsps(): UseUspsState {
  const client = useApiClient();
  const [result, setResult] = useState<UspsAddressResponse | undefined>(undefined);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [autoFilling, setAutoFilling] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllers = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const set = controllers.current;
    return () => {
      set.forEach((c) => {
        try { c.abort(); } catch { /* ignore */ }
      });
      set.clear();
    };
  }, []);

  const makeSignal = useCallback((): AbortSignal => {
    const c = new AbortController();
    controllers.current.add(c);
    return c.signal;
  }, []);

  const reset = useCallback((): void => {
    setResult(undefined);
    setError(null);
  }, []);

  const validateAddress = useCallback(
    async (q: UspsAddressInput): Promise<UspsAddressResponse | undefined> => {
      if (!q.streetAddress || !q.state) return undefined;
      setVerifying(true);
      setError(null);
      const signal = makeSignal();
      try {
        const body: Record<string, unknown> = {
          streetAddress: q.streetAddress,
          state: q.state,
        };
        if (q.secondaryAddress) body['secondaryAddress'] = q.secondaryAddress;
        if (q.city) body['city'] = q.city;
        if (q.ZIPCode) body['ZIPCode'] = q.ZIPCode;
        const resp = await client.usps.address(body as never, { signal });
        setResult(resp);
        // Error may live at resp.error OR as a top-level {code, message, errors}
        // envelope when USPS returned a 4xx and the Function passed it through.
        const rawErr =
          (resp as { error?: unknown }).error ??
          (('code' in (resp as object) && 'message' in (resp as object)) ? resp : undefined);
        const coerced = coerceUspsError(rawErr);
        if (coerced) setError(coerced);
        return resp;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return undefined;
        // ApiError has a string .message; anything else -> coerce.
        const msg =
          err instanceof Error
            ? err.message
            : coerceUspsError(err) ?? 'USPS validation failed';
        setError(msg);
        return undefined;
      } finally {
        setVerifying(false);
      }
    },
    [client, makeSignal],
  );

  const autoFillFromZip = useCallback(
    async (zip: string): Promise<{ city: string; state: string; ZIPCode: string } | undefined> => {
      const clean = zip.replace(/\D/g, '').slice(0, 5);
      if (clean.length !== 5) return undefined;
      setAutoFilling(true);
      const signal = makeSignal();
      try {
        const resp = await client.usps.cityState(clean, { signal });
        if (resp && typeof resp.city === 'string' && typeof resp.state === 'string') {
          return { city: resp.city, state: resp.state, ZIPCode: resp.ZIPCode };
        }
        return undefined;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return undefined;
        // Silent for autofill — not every ZIP is indexed, fall back to manual.
        return undefined;
      } finally {
        setAutoFilling(false);
      }
    },
    [client, makeSignal],
  );

  return { result, verifying, autoFilling, error, validateAddress, autoFillFromZip, reset };
}
